import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmarModal from '../components/ConfirmarModal';
import FiscalRelatorioPrevia from '../components/FiscalRelatorioPrevia';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Tarefas Extras (port do ZADRA). Demandas internas com histórico (comentário
// obrigatório em cada atualização). "Enviar ao Acessórias" cria a solicitação interna lá:
// primeiro mostra o que será enviado, depois envia. Tarefa já no Acessórias: cada
// atualização também vai para lá (com confirmação) e ela não pode mais ser excluída.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const STATUS = ['Pendente', 'Resolvendo', 'Concluído'];
const NOVA = { empresa_cod: '', assunto: '', prazo: '', prioridade: 2, responsavel: '', descricao: '' };
const ROTULO_CAMPO = { assunto: 'Assunto', empresa: 'Empresa (CNPJ)', departamento: 'Departamento', prioridade: 'Prioridade', descricao: 'Descrição', data_prazo: 'Prazo', tipo: 'Tipo', statusSol: 'Status' };

const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');

function SeloStatus({ status }) {
  if (status === 'Concluído') return <span className="fz-selo ok"><i className="fas fa-circle-check"></i>Concluído</span>;
  if (status === 'Resolvendo') return <span className="fz-selo alerta"><i className="fas fa-spinner"></i>Resolvendo</span>;
  return <span className="fz-selo neutro"><i className="fas fa-clock"></i>Pendente</span>;
}

function Payload({ payload, prioridades }) {
  return (
    <dl className="fz-conferencia row mx-0 mb-0">
      {Object.entries(payload).map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="col-sm-4">{ROTULO_CAMPO[k] || k}</dt>
          <dd className="col-sm-8" style={{ overflowWrap: 'anywhere' }}>
            {k === 'prioridade' ? (prioridades[v] || v) : k === 'tipo' && v === 'I' ? 'Interna' : k === 'data_prazo' ? dataBr(v) : k === 'statusSol' ? { R: 'Resolvendo', F: 'Concluído' }[v] : (v || '—')}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export default function FiscalTarefas() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [ocultarConcluidas, setOcultarConcluidas] = useState(true);
  const [nova, setNova] = useState(null);
  const [painel, setPainel] = useState(null);       // { tarefa, historico, form }
  const [confirmAtualizacao, setConfirmAtualizacao] = useState(null); // payload a enviar ao Acessórias
  const [envio, setEnvio] = useState(null);         // { tarefa, payload } ensaio do envio
  const [excluir, setExcluir] = useState(null);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/tarefas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as tarefas'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const prioridades = dados?.prioridades || {};
  const responsaveis = useMemo(() => [...new Set((dados?.tarefas || []).map((t) => t.responsavel).filter(Boolean))].sort(), [dados]);
  const tarefas = useMemo(() => {
    if (!dados) return [];
    const termo = semAcento(busca.trim());
    return dados.tarefas.filter((t) => {
      if (ocultarConcluidas && t.status === 'Concluído') return false;
      if (responsavel && t.responsavel !== responsavel) return false;
      return !termo || semAcento(t.cliente).includes(termo) || semAcento(t.assunto).includes(termo) || String(t.cod) === termo;
    });
  }, [dados, busca, ocultarConcluidas, responsavel]);

  const trocarTarefa = (t) => setDados((d) => ({ ...d, tarefas: d.tarefas.map((x) => (x.id === t.id ? t : x)) }));

  // ---- nova ----
  const salvarNova = () => {
    setProcessando(true);
    axios.post(`${API}/api/fiscal/tarefas/`, nova, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setNova(null); setDados((d) => ({ ...d, tarefas: [r.data.tarefa, ...d.tarefas] })); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível criar a tarefa'))
      .finally(() => setProcessando(false));
  };

  // ---- histórico + atualização ----
  const abrirPainel = (t) => {
    setPainel({ tarefa: t, historico: null, form: { status: t.status, responsavel: t.responsavel || '', prazo: t.prazo || '', comentario: '' } });
    axios.get(`${API}/api/fiscal/tarefas/${t.id}/historico/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setPainel((p) => (p && p.tarefa.id === t.id ? { ...p, historico: r.data.historico } : p)))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar o histórico'));
  };
  const setForm = (campo, valor) => setPainel((p) => ({ ...p, form: { ...p.form, [campo]: valor } }));
  const enviarAtualizacao = (ensaio) => {
    setProcessando(true);
    axios.patch(`${API}/api/fiscal/tarefas/${painel.tarefa.id}/`, { ...painel.form, ensaio }, { headers: headers() })
      .then((r) => {
        if (r.data.ensaio) { setConfirmAtualizacao(r.data.payload_acessorias); return; }
        trocarTarefa(r.data.tarefa); setMsg(r.data.mensagem); setPainel(null); setConfirmAtualizacao(null);
      })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível atualizar'); setConfirmAtualizacao(null); })
      .finally(() => setProcessando(false));
  };

  // ---- envio ao Acessórias: ensaio -> confirmação ----
  const ensaiarEnvio = (t) => {
    setProcessando(true);
    axios.post(`${API}/api/fiscal/tarefas/${t.id}/acessorias/`, { ensaio: true }, { headers: headers() })
      .then((r) => setEnvio({ tarefa: t, payload: r.data.payload }))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível preparar o envio'))
      .finally(() => setProcessando(false));
  };
  const confirmarEnvio = () => {
    setProcessando(true);
    axios.post(`${API}/api/fiscal/tarefas/${envio.tarefa.id}/acessorias/`, { ensaio: false }, { headers: headers() })
      .then((r) => { trocarTarefa(r.data.tarefa); setMsg(r.data.mensagem); setEnvio(null); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível enviar ao Acessórias'); setEnvio(null); })
      .finally(() => setProcessando(false));
  };

  const confirmarExclusao = () => {
    setProcessando(true);
    axios.delete(`${API}/api/fiscal/tarefas/${excluir.id}/`, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setDados((d) => ({ ...d, tarefas: d.tarefas.filter((t) => t.id !== excluir.id) })); setExcluir(null); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível excluir'); setExcluir(null); })
      .finally(() => setProcessando(false));
  };

  const classePrazo = (t) => (t.situacao_prazo === 'vencida' ? 'text-danger fw-bold' : t.situacao_prazo === 'proxima' ? 'fz-prazo-proximo' : '');
  const usuarios = dados?.usuarios || [];
  const novaValida = nova && nova.empresa_cod && nova.assunto.trim() && nova.prazo && nova.responsavel;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-list-check header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Tarefas Extras</h2>
          </div>
          <div className="gr-toolbar">
            <FiscalRelatorioPrevia />
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            <button type="button" className="gr-btn primaria" onClick={() => setNova({ ...NOVA, responsavel: dados?.meu_username || '' })} disabled={!dados}>
              <i className="fas fa-plus" aria-hidden="true"></i>Nova tarefa
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="fz-filtros">
          <input className="po-busca" placeholder="Buscar por empresa, código ou assunto" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="po-toggle"><input type="checkbox" checked={ocultarConcluidas} onChange={(e) => setOcultarConcluidas(e.target.checked)} />Ocultar concluídas</label>
        </div>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando tarefas...</div></div>
        ) : (
          <section className="po-card po-enter">
            <header className="po-card-head">
              <h3><i className="fas fa-list-check" aria-hidden="true"></i>Tarefas</h3>
              <span className="badge bg-light text-dark">{tarefas.length} de {dados?.tarefas?.length || 0}</span>
            </header>
            <div className="po-card-body">
              <div className="po-scroll" style={{ maxHeight: 'none', overflow: 'visible' }}>
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Empresa</th><th>Assunto</th><th>Responsável</th><th>Prioridade</th><th>Prazo</th><th>Status</th><th>Acessórias</th><th></th></tr></thead>
                  <tbody>
                    {tarefas.map((t) => (
                      <tr key={t.id}>
                        <td><div className="fw-semibold">{t.cliente}</div><div className="fz-sub fz-num">{t.cod}</div></td>
                        <td>{t.assunto}{t.descricao && <div className="fz-sub text-truncate" style={{ maxWidth: 320 }} title={t.descricao}>{t.descricao}</div>}</td>
                        <td className="small">{t.responsavel || '—'}</td>
                        <td className="small">{t.prioridade_nome}</td>
                        <td className={`fz-num ${classePrazo(t)}`} title={t.situacao_prazo === 'vencida' ? 'Prazo vencido' : t.situacao_prazo === 'proxima' ? 'Vence em até 3 dias' : undefined}>{dataBr(t.prazo)}</td>
                        <td><SeloStatus status={t.status} /></td>
                        <td className="small fz-num">{t.acessorias_id ? `#${t.acessorias_id}` : <span className="text-muted">não enviada</span>}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-outline-primary me-1" title="Histórico e atualização" onClick={() => abrirPainel(t)}><i className="fas fa-comments"></i></button>
                          {!t.acessorias_id && (
                            <>
                              <button className="btn btn-sm btn-outline-secondary me-1" title="Enviar ao Acessórias" onClick={() => ensaiarEnvio(t)} disabled={processando}><i className="fas fa-paper-plane"></i></button>
                              <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => setExcluir(t)}><i className="fas fa-trash"></i></button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {tarefas.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">Nenhuma tarefa com esses filtros.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {nova && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-plus me-2" style={{ color: '#00b5cc' }}></i>Nova tarefa</h5>
                  <button type="button" className="btn-close" onClick={() => setNova(null)} disabled={processando}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Empresa</label>
                      <select className="form-select" value={nova.empresa_cod} onChange={(ev) => setNova((f) => ({ ...f, empresa_cod: ev.target.value }))}>
                        <option value="">— escolha —</option>
                        {(dados?.empresas || []).map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Assunto</label>
                      <input className="form-control" maxLength={200} value={nova.assunto} onChange={(ev) => setNova((f) => ({ ...f, assunto: ev.target.value }))} />
                      {nova.assunto.length > 100 && <div className="form-text text-warning">O Acessórias guarda só os 100 primeiros caracteres.</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Prazo</label>
                      <input type="date" className="form-control" value={nova.prazo} onChange={(ev) => setNova((f) => ({ ...f, prazo: ev.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Prioridade</label>
                      <select className="form-select" value={nova.prioridade} onChange={(ev) => setNova((f) => ({ ...f, prioridade: Number(ev.target.value) }))}>
                        {[2, 1, 0, 3].map((p) => <option key={p} value={p}>{prioridades[p]}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Responsável</label>
                      <select className="form-select" value={nova.responsavel} onChange={(ev) => setNova((f) => ({ ...f, responsavel: ev.target.value }))}>
                        <option value="">— escolha —</option>
                        {usuarios.map((u) => <option key={u.id} value={u.username}>{u.username}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Descrição</label>
                      <textarea className="form-control" rows={4} value={nova.descricao} onChange={(ev) => setNova((f) => ({ ...f, descricao: ev.target.value }))} />
                    </div>
                  </div>
                  <div className="form-text mt-2">A tarefa nasce só no Saber. Para levá-la ao Acessórias, use o botão <i className="fas fa-paper-plane"></i> na lista.</div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setNova(null)} disabled={processando}><i className="fas fa-times me-2"></i>Cancelar</button>
                  <button className="btn btn-primary" onClick={salvarNova} disabled={processando || !novaValida}>
                    <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{processando ? 'Salvando...' : 'Criar tarefa'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {painel && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-comments me-2" style={{ color: '#00b5cc' }}></i>{painel.tarefa.assunto}</h5>
                    <small style={{ color: '#6c757d' }}>{painel.tarefa.cod} · {painel.tarefa.cliente}{painel.tarefa.acessorias_id ? ` · Acessórias #${painel.tarefa.acessorias_id}` : ''}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setPainel(null)} disabled={processando}></button>
                </div>
                <div className="modal-body">
                  {painel.tarefa.descricao && <p className="small" style={{ whiteSpace: 'pre-wrap' }}>{painel.tarefa.descricao}</p>}
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Status</label>
                      <select className="form-select" value={painel.form.status} onChange={(ev) => setForm('status', ev.target.value)}>
                        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Responsável</label>
                      <select className="form-select" value={painel.form.responsavel} onChange={(ev) => setForm('responsavel', ev.target.value)}>
                        <option value="">— escolha —</option>
                        {usuarios.map((u) => <option key={u.id} value={u.username}>{u.username}</option>)}
                        {painel.form.responsavel && !usuarios.some((u) => u.username === painel.form.responsavel) && <option value={painel.form.responsavel} disabled>{painel.form.responsavel} (sem login)</option>}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Prazo</label>
                      <input type="date" className="form-control" value={painel.form.prazo} onChange={(ev) => setForm('prazo', ev.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Comentário <span className="text-danger">*</span></label>
                      <textarea className="form-control" rows={3} value={painel.form.comentario} onChange={(ev) => setForm('comentario', ev.target.value)} placeholder="O que foi feito ou o que mudou" />
                      {painel.tarefa.acessorias_id && <div className="form-text">Esta tarefa está no Acessórias: a atualização também vai para lá (você confirma antes).</div>}
                    </div>
                  </div>
                  <h6 className="fw-bold small text-uppercase" style={{ color: '#6c757d' }}>Histórico</h6>
                  {painel.historico === null ? (
                    <div className="text-muted small"><i className="fas fa-spinner fa-spin me-2"></i>Carregando...</div>
                  ) : painel.historico.length === 0 ? (
                    <div className="text-muted small">Sem atualizações ainda.</div>
                  ) : painel.historico.map((h, i) => (
                    <div className="fz-acesso" key={`${h.data_hora}-${i}`}>
                      <div className="fz-sub"><b>{h.usuario}</b> · {h.data_hora}</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{h.comentario}</div>
                    </div>
                  ))}
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setPainel(null)} disabled={processando}><i className="fas fa-times me-2"></i>Fechar</button>
                  <button className="btn btn-primary" onClick={() => enviarAtualizacao(true)}
                    disabled={processando || !painel.form.comentario.trim() || !painel.form.responsavel}>
                    <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar atualização
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmarModal aberto={!!confirmAtualizacao} titulo="Atualizar também no Acessórias" rotuloConfirmar="Enviar e salvar" processando={processando}
          texto={confirmAtualizacao && (<><p className="mb-2">Esta atualização será enviada à solicitação <b>#{painel?.tarefa?.acessorias_id}</b> do Acessórias. Se ele recusar, nada muda no Saber.</p><Payload payload={confirmAtualizacao} prioridades={prioridades} /></>)}
          onConfirmar={() => enviarAtualizacao(false)} onCancelar={() => setConfirmAtualizacao(null)} />

        <ConfirmarModal aberto={!!envio} titulo="Enviar ao Acessórias" rotuloConfirmar="Enviar" processando={processando}
          texto={envio && (<><p className="mb-2">Será criada esta solicitação interna no Acessórias. Depois do envio, a tarefa não pode mais ser excluída pelo Saber.</p><Payload payload={envio.payload} prioridades={prioridades} /></>)}
          onConfirmar={confirmarEnvio} onCancelar={() => setEnvio(null)} />

        <ConfirmarModal aberto={!!excluir} perigo titulo="Excluir tarefa" rotuloConfirmar="Excluir" processando={processando}
          texto={excluir && <>Excluir a tarefa <b>{excluir.assunto}</b> de {excluir.cliente}? O histórico é apagado junto.</>}
          onConfirmar={confirmarExclusao} onCancelar={() => setExcluir(null)} />
      </div>
    </div>
  );
}
